import { NoteComposer } from '@/components/notes/note-composer';
import { NoteThread } from '@/components/notes/note-thread';
import { emptyTargetMessage } from '@/lib/note-format';
import {
  type NoteViewer,
  type NotesScope,
  isThreadResolved,
  targetKey,
  threadsForTarget,
  useCreateNote,
  useNoteActions,
  useScopeNotes,
} from '@/lib/notes-queries';
import { cn } from '@/lib/utils';
import type { NoteTarget } from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';
import { useState } from 'react';

export interface NotesTargetPanelProps {
  /** Where the discussions live: a matrix or a folder. */
  scope: NotesScope;
  /** The spot the panel is about; the matrix (or the folder) itself when omitted. */
  target?: NoteTarget;
  /**
   * The label of the target (`targetLabel` of its discussions): « Suivi après la
   * visite × Barre des minimas ». Falls back to the label of a discussion, if any.
   */
  targetLabel?: string;
  companyId: string;
  /** Named in « Visible par » and in the level picker. */
  companyName: string;
  viewer: NoteViewer;
  /**
   * Offers the field that writes a new discussion (default). The read-only global view of
   * the grid turns it off: it lets one read, answer and resolve, not create.
   */
  allowNewNote?: boolean;
  /** Focuses the composer when it mounts and each time this value changes (« Ajouter une note »). */
  focusComposerKey?: number;
  /** Shows the link to the whole notes view of the matrix. */
  onOpenAllNotes?: () => void;
  /** Wording of that link. */
  allNotesLabel?: string;
  /** The clock of the relative dates; tests pin it. */
  now?: Date;
  className?: string;
}

function defaultTarget(scope: NotesScope): NoteTarget {
  return scope.type === 'folder' ? { type: 'folder' } : { type: 'matrix' };
}

const TYPE_WORDS: Record<NoteTarget['type'], string> = {
  matrix: 'Matrice entière',
  cell: 'Case',
  row: 'Ligne',
  column: 'Colonne',
  folder: 'Dossier',
};

function ScopeLine({ target, label }: { target: NoteTarget; label: string }) {
  if (target.type === 'matrix')
    return <p className="text-caption text-gray-600">{TYPE_WORDS.matrix}</p>;
  if (label === '') return <p className="text-caption text-gray-600">{TYPE_WORDS[target.type]}</p>;
  const prefix = target.type === 'cell' ? 'Case ' : '';
  return (
    <p className="text-caption text-gray-600">
      {prefix}
      <b className="font-semibold text-noir">{label}</b>
    </p>
  );
}

/**
 * What a target's notes look like wherever they are shown: the Notes tab of
 * the drawer, the anchored bubble of the grid and the notes panel of a folder
 * (planches 09, 11, 15). The target label, its open discussions, the field to
 * write a new one, the folded « N notes résolues », an empty state worded for
 * the type of target, and an optional link to all the notes of the matrix.
 *
 * It reads and writes through `notes-queries`, so it needs a
 * `QueryClientProvider` and nothing else.
 */
export function NotesTargetPanel({
  scope,
  target: targetProp,
  targetLabel,
  companyId,
  companyName,
  viewer,
  allowNewNote = true,
  focusComposerKey,
  onOpenAllNotes,
  allNotesLabel = 'Toutes les notes de la matrice',
  now,
  className,
}: NotesTargetPanelProps) {
  const target = targetProp ?? defaultTarget(scope);
  const notes = useScopeNotes(scope);
  const actions = useNoteActions(scope, companyId);
  const create = useCreateNote(scope, companyId);
  const [resolvedOpen, setResolvedOpen] = useState(false);

  const targetThreads = threadsForTarget(notes.threads, target);
  const openThreads = targetThreads.filter((thread) => !isThreadResolved(thread));
  const resolvedThreads = targetThreads.filter(isThreadResolved);
  const label = targetLabel ?? targetThreads[0]?.targetLabel ?? '';
  const ready = !notes.isPending && !notes.isError;

  return (
    <section
      key={targetKey(target)}
      aria-label="Notes"
      className={cn('flex flex-col gap-3 text-caption', className)}
    >
      <ScopeLine target={target} label={label} />

      {notes.isPending && <p className="text-gray-600">Chargement des notes…</p>}
      {notes.isError && (
        <p role="alert" className="text-rouge-deep">
          Les notes n'ont pas pu être chargées.{' '}
          <button
            type="button"
            onClick={notes.refetch}
            className="font-semibold underline underline-offset-2"
          >
            Réessayer
          </button>
        </p>
      )}
      {ready && targetThreads.length === 0 && (
        <p className="text-gray-600">{emptyTargetMessage(target.type)}</p>
      )}
      {ready &&
        openThreads.map((thread) => (
          <NoteThread
            key={thread.id}
            thread={thread}
            viewer={viewer}
            companyName={companyName}
            actions={actions}
            {...(now ? { now } : {})}
          />
        ))}

      {allowNewNote && (
        <NoteComposer
          key={targetKey(target)}
          mode="note"
          role={viewer.role}
          companyName={companyName}
          {...(focusComposerKey !== undefined ? { focusKey: focusComposerKey } : {})}
          onSubmit={({ body, visibility }) =>
            create.mutateAsync({
              ...(target.type === 'folder' ? {} : { target }),
              body,
              visibility,
            })
          }
        />
      )}

      {ready && resolvedThreads.length > 0 && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            aria-expanded={resolvedOpen}
            onClick={() => setResolvedOpen((open) => !open)}
            className="flex items-center gap-1 self-start rounded-sm text-caption text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            {resolvedOpen ? (
              <Icon name="expand_more" size={14} />
            ) : (
              <Icon name="chevron_right" size={14} />
            )}
            {resolvedThreads.length === 1
              ? '1 note résolue'
              : `${resolvedThreads.length} notes résolues`}
          </button>
          {resolvedOpen &&
            resolvedThreads.map((thread) => (
              <NoteThread
                key={thread.id}
                thread={thread}
                viewer={viewer}
                companyName={companyName}
                actions={actions}
                {...(now ? { now } : {})}
              />
            ))}
        </div>
      )}

      {onOpenAllNotes && (
        <button
          type="button"
          onClick={onOpenAllNotes}
          className="self-start rounded-sm text-bleu-nuit text-caption underline underline-offset-2 hover:text-bleu-nuit-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
        >
          {allNotesLabel}
        </button>
      )}
    </section>
  );
}
